import React from 'react';
import { Autocomplete, Box, Checkbox } from '@mui/material';
import { SearchTextField } from 'src/components/BeaconV2Components/TableSearch';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { FieldValues, useFormContext, Controller, Path, PathValue } from 'react-hook-form';
import colors from 'src/utils/colors';
import FilterChip from './FilterChip';
import { useAppSelector } from 'src/utils/Hooks/typedHooks';

type EmailAutocompleteProps<T extends FieldValues> = {
  name: Path<T>;
  placeholder?: string;
};

const DEFAULT_MAX_EMAILS = 10;

function EmailAutocomplete<T extends FieldValues>({
  name,
  placeholder = 'Add emails, separated by pressing Enter'
}: EmailAutocompleteProps<T>) {
  const { control, formState } = useFormContext<T>();
  const idBase = `email-autocomplete-${String(name)}`;

  // Get error state for this field using the full path
  const getFieldError = (errors: any, path: string) => {
    return path.split('.').reduce((current, key) => current?.[key], errors);
  };

  const fieldError = getFieldError(formState.errors, name);
  const hasError = !!fieldError;

  return (
    <>
      <Box
        sx={{
          border: `2px solid ${hasError ? colors.errorRed : colors.borderGrey}`,
          borderRadius: '4px',
          backgroundColor: 'white',
          overflow: 'hidden', // Ensures inner content doesn't overflow the rounded corners
          '&:focus-within': {
            borderColor: hasError ? colors.errorRed : colors.darkerGrey
          }
        }}
      >
        <Controller
          name={name}
          control={control}
          render={({ field }) => {
            const displayValue = Array.isArray(field.value) ? field.value : [];

            return (
              <Autocomplete
                options={[]} // No predefined options for freeSolo email input
                freeSolo
                openOnFocus={false}
                forcePopupIcon={false}
                multiple
                disableCloseOnSelect
                disableClearable
                autoSelect={false}
                autoHighlight={false}
                autoComplete={false}
                value={displayValue}
                onChange={(_, newValue) => {
                  // Limit to maxEmails
                  const emails = (newValue as string[]).slice(0, DEFAULT_MAX_EMAILS);
                  field.onChange(emails);
                }}
                renderInput={(params) => {
                  const hasEmails = displayValue.length > 0;
                  const currentPlaceholder = hasEmails ? 'Enter to add' : placeholder;

                  return (
                    <SearchTextField
                      {...params}
                      fullWidth
                      size="small"
                      placeholder={currentPlaceholder}
                      autoFocus={false}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '2px' // Slightly smaller than parent to avoid visual conflicts
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                          border: 'none'
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          border: 'none'
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          border: 'none'
                        }
                      }}
                    />
                  );
                }}
                sx={{
                  '& .MuiInputBase-root': {
                    minHeight: '80px',
                    padding: '12px',
                    backgroundColor: 'transparent',
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'flex-start',
                    alignContent: 'flex-start',
                    gap: '4px'
                  },
                  '& .MuiAutocomplete-inputRoot': {
                    alignItems: 'flex-start',
                    alignContent: 'flex-start',
                    minHeight: '80px'
                  },
                  '& .MuiAutocomplete-endAdornment': {
                    display: 'none' // Hide dropdown arrow for freeSolo
                  },
                  '& .MuiInputBase-input': {
                    padding: 0,
                    fontSize: '14px',
                    height: '28px',
                    lineHeight: '28px',
                    alignSelf: 'flex-start',
                    minWidth: '200px',
                    flex: '1 1 200px'
                  },
                  '& .MuiAutocomplete-tag': {
                    alignSelf: 'flex-start',
                    margin: '2px 4px 2px 0',
                    flex: '0 0 auto'
                  }
                }}
                renderTags={(selected) => {
                  // Show all emails as chips (up to DEFAULT_MAX_EMAILS limit enforced in onChange)
                  return selected.map((email, i) => (
                    <FilterChip
                      key={`${idBase}-${email}-${i}`}
                      keyProp={`${idBase}-${email}-${i}`}
                      label={String(email)}
                      onDelete={() => {
                        // Remove this specific email from the array
                        const updatedEmails = selected.filter((_, index) => index !== i);
                        field.onChange(updatedEmails);
                      }}
                    />
                  ));
                }}
              />
            );
          }}
        />
      </Box>
      {/* Error Text */}
      {hasError && fieldError && (
        <Text
          variant="body3"
          sx={{
            color: colors.errorRed,
            fontSize: '12px'
          }}
        >
          {fieldError.message || 'One or more emails are invalid'}
        </Text>
      )}
    </>
  );
}

export default EmailAutocomplete;
